import { env } from "@/lib/env"
import { resolveApiUrl } from "@/lib/api-url"
import { fetchWithAuth } from "@/services/api.client"
import type { DocFrame, EventFrame } from "./contract"

export const BASE = "/api/v1/oio-sim"
export const oioUrl = (path: string) => resolveApiUrl(env.VITE_API_URL, `${BASE}${path}`)

/** Reconnect delays for followLog, capped at the last value. */
export const BACKOFF_MS = [1000, 2000, 4000, 8000]

/** Non-2xx response from an oio-sim route; `status` lets callers tell 401/404/409/422 apart. */
export class OioSimHttpError extends Error {
  constructor(public readonly status: number, public readonly detail: string, public readonly requestId?: string) {
    super(detail || `HTTP ${status}`)
    this.name = "OioSimHttpError"
  }
}

/** A run frame skipped ahead of the cursor (thread_seq > cursor + 1). The follow restarts from the cursor. */
export class GapDetected extends Error {
  constructor(public readonly cursor: number, public readonly received: number) {
    super(`gap after thread_seq ${cursor}: received ${received}`)
    this.name = "GapDetected"
  }
}

/** Consecutive gap restarts with no progress before the follow gives up. */
export const MAX_GAP_RESTARTS = 5

/** The follow gave up after MAX_GAP_RESTARTS restarts in a row that did not advance the cursor. */
export class GapUnrecoverable extends Error {
  constructor(public readonly cursor: number, public readonly restarts: number) {
    super(`gap at thread_seq ${cursor} not repaired after ${restarts} restarts`)
    this.name = "GapUnrecoverable"
  }
}

export async function httpError(res: Response): Promise<OioSimHttpError> {
  let detail = ""
  try {
    const body: unknown = await res.json()
    const d = body && typeof body === "object" ? (body as { detail?: unknown }).detail : undefined
    if (typeof d === "string") detail = d
    else if (d !== undefined) detail = JSON.stringify(d)
  } catch {
    detail = res.statusText
  }
  return new OioSimHttpError(res.status, detail, res.headers?.get?.("X-Request-Id") || undefined)
}

/** Parse `data:` frames from a fetch body into JSON values. Comments (heartbeats) and bad frames are skipped. */
export async function* parseFrames(stream: ReadableStream<Uint8Array>): AsyncGenerator<unknown> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let dataLines: string[] = []

  const dispatch = (): unknown => {
    if (dataLines.length === 0) return undefined
    const data = dataLines.join("\n")
    dataLines = []
    try {
      return JSON.parse(data)
    } catch {
      return undefined
    }
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value, { stream: !done })
      // On the final read `buffer` is never popped, so an unterminated last line is in `lines` too.
      const lines = buffer.split(/\r?\n/)
      buffer = done ? "" : lines.pop() ?? ""
      for (const line of lines) {
        if (line === "") {
          const parsed = dispatch()
          if (parsed !== undefined) yield parsed
          continue
        }
        if (line.startsWith(":")) continue
        const separator = line.indexOf(":")
        const field = separator < 0 ? line : line.slice(0, separator)
        if (field === "data") dataLines.push(separator < 0 ? "" : line.slice(separator + 1).replace(/^ /, ""))
      }
      if (done) {
        const parsed = dispatch()
        if (parsed !== undefined) yield parsed
        break
      }
    }
  } finally {
    // Stop the body when the consumer stops early (gap restart, handler error, abort).
    await reader.cancel().catch(() => {})
    reader.releaseLock()
  }
}

/** How to recognise a frame of one stream and read its replay cursor. */
export interface FrameSpec<T> {
  is: (value: unknown) => value is T
  seq: (frame: T) => number
  /** Run events must arrive with no gaps. Document seqs may skip (coalescing), so only increasing order is enforced. */
  consecutive: boolean
}

const hasType = (v: { type?: unknown }) => typeof v.type === "string"

export const RUN_FRAMES: FrameSpec<EventFrame> = {
  is: (value): value is EventFrame =>
    !!value && typeof value === "object" && hasType(value as { type?: unknown })
    && typeof (value as { thread_seq?: unknown }).thread_seq === "number",
  seq: (frame) => frame.thread_seq,
  consecutive: true,
}

export const DOC_FRAMES: FrameSpec<DocFrame> = {
  is: (value): value is DocFrame =>
    !!value && typeof value === "object" && hasType(value as { type?: unknown })
    && typeof (value as { seq?: unknown }).seq === "number",
  seq: (frame) => frame.seq,
  consecutive: false,
}

export interface StreamResult { ended: "eof" | "no_content"; lastSeq: number }

/** Fetch one SSE route and hand each valid frame to `onFrame`. A 204 means nothing to replay. */
export async function streamFrames<T>(
  url: string,
  init: RequestInit,
  spec: FrameSpec<T>,
  onFrame: (frame: T) => void,
  signal?: AbortSignal,
): Promise<StreamResult> {
  const res = await fetchWithAuth(url, { ...init, signal, credentials: "include" })
  if (res.status === 204) return { ended: "no_content", lastSeq: -1 }
  if (!res.ok) throw await httpError(res)
  if (!res.body) return { ended: "eof", lastSeq: -1 }
  let lastSeq = -1
  for await (const value of parseFrames(res.body)) {
    if (!spec.is(value)) continue
    lastSeq = Math.max(lastSeq, spec.seq(value))
    onFrame(value)
  }
  return { ended: "eof", lastSeq }
}

/** `X-Session-Id` for a `/threads/<id>/...` route, so the gateway can correlate the stream. */
function sessionHeader(logPath: string): Record<string, string> {
  const m = /^\/threads\/([^/]+)/.exec(logPath)
  return m ? { "X-Session-Id": decodeURIComponent(m[1]) } : {}
}

/** Resolves after `ms`, or as soon as `signal` aborts. The timer is cleared on abort. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) return resolve()
    const done = () => {
      clearTimeout(timer)
      signal?.removeEventListener("abort", done)
      resolve()
    }
    const timer = setTimeout(done, ms)
    signal?.addEventListener("abort", done, { once: true })
  })
}

/**
 * Follow an event log (`<logPath>?after=`) from `afterSeq`, live, until `signal` aborts.
 *
 * - The cursor advances only after `onEvent` returns. An error thrown by `onEvent` rejects the follow.
 * - Frames at or below the cursor are dropped. A run frame past cursor + 1 restarts the follow from the cursor.
 *   After MAX_GAP_RESTARTS restarts in a row with no progress, `onError` gets a GapUnrecoverable and the follow stops.
 *   Without `onError` that error is thrown.
 * - A dropped or ended stream reconnects from the cursor after 1s, 2s, 4s, 8s (capped). The backoff resets on progress.
 *   The backoff sleep ends early on abort.
 * - 4xx responses (auth, not found) are thrown, not retried.
 */
export async function followLog<T>(
  logPath: string,
  afterSeq: number,
  spec: FrameSpec<T>,
  onEvent: (frame: T) => void,
  signal?: AbortSignal,
  delaysMs: number[] = BACKOFF_MS,
  onError?: (error: GapUnrecoverable) => void,
): Promise<void> {
  let cursor = afterSeq
  let attempt = 0
  let gapRestarts = 0
  while (!signal?.aborted) {
    const handler: { failed: boolean; error?: unknown } = { failed: false }
    try {
      await streamFrames(
        oioUrl(`${logPath}?after=${cursor}`),
        { headers: { Accept: "text/event-stream", ...sessionHeader(logPath) } },
        spec,
        (frame) => {
          if (signal?.aborted) return
          const seq = spec.seq(frame)
          if (seq <= cursor) return
          if (spec.consecutive && seq > cursor + 1) throw new GapDetected(cursor, seq)
          try {
            onEvent(frame)
          } catch (error) {
            handler.failed = true
            handler.error = error
            throw error
          }
          cursor = seq
          attempt = 0
          gapRestarts = 0
        },
        signal,
      )
    } catch (err) {
      if (handler.failed) throw handler.error
      if (err instanceof OioSimHttpError && err.status >= 400 && err.status < 500) throw err
      if (signal?.aborted) return
      if (err instanceof GapDetected) {
        gapRestarts += 1
        if (gapRestarts > MAX_GAP_RESTARTS) {
          const unrecoverable = new GapUnrecoverable(cursor, gapRestarts - 1)
          if (!onError) throw unrecoverable
          onError(unrecoverable)
          return
        }
      }
      // GapDetected, network drop or 5xx: fall through to the backoff and reconnect from the cursor.
    }
    if (signal?.aborted) return
    await sleep(delaysMs[Math.min(attempt, delaysMs.length - 1)] ?? 0, signal)
    attempt += 1
  }
}

/** Live run events for one thread, replayed from `afterSeq`. See followLog for gap and error handling. */
export function followThread(
  threadId: string,
  afterSeq: number,
  onEvent: (frame: EventFrame) => void,
  signal?: AbortSignal,
  delaysMs?: number[],
  onError?: (error: GapUnrecoverable) => void,
): Promise<void> {
  return followLog(`/threads/${encodeURIComponent(threadId)}/events`, afterSeq, RUN_FRAMES, onEvent, signal, delaysMs, onError)
}

/** Live document progress events for one thread, replayed from `afterSeq`. */
export function followDocuments(
  threadId: string,
  afterSeq: number,
  onEvent: (frame: DocFrame) => void,
  signal?: AbortSignal,
  delaysMs?: number[],
): Promise<void> {
  return followLog(`/threads/${encodeURIComponent(threadId)}/documents/events`, afterSeq, DOC_FRAMES, onEvent, signal, delaysMs)
}
